import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import CustomerTypeBadge from '../../components/admin/CustomerTypeBadge';
import CustomerStatusBadge from '../../components/admin/CustomerStatusBadge';
import CustomerActionsMenu from '../../components/admin/CustomerActionsMenu';
import { adminApi } from '../../lib/api';
import { useAuth } from '../../App';
import { formatDateTime, getTodayString, getDateDaysAgo } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { LoadingBlock, EmptyState } from '../../components/common/States';
import AdminCreateCustomerDialog from '../../components/admin/AdminCreateCustomerDialog';

const STATUS_TABS = [
  { value: 'active',    label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'banned',    label: 'Banned' },
  { value: 'deleted',   label: 'Deleted' },
  { value: 'all',       label: 'All' },
];

export default function AdminCustomersPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const role = user?.role;

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    status: 'active',           // segmented tab; replaces is_active dropdown
    customer_type: 'all',
    date_from: getDateDaysAgo(90),
    date_to: getTodayString(),
    page: 1,
    limit: 20,
  });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.customer_type === 'all') delete params.customer_type;
      if (!params.search) delete params.search;
      const res = await adminApi.getCustomers(params);
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  return (
    <AdminShell
      title="Customers"
      actions={
        ['super_admin', 'manager', 'reception', 'marketing'].includes(role) ? (
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="bg-[#D4A843] text-black hover:brightness-110 gap-1.5"
            data-testid="admin-customers-new-button"
          >
            <UserPlus size={14} />
            New Customer
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Status tabs */}
        <div
          className="flex items-center gap-1 surface-card card-highlight rounded-xl p-1 w-fit"
          data-testid="customers-status-tabs"
        >
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setF('status', tab.value)}
              data-testid={`customers-status-tab-${tab.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filters.status === tab.value
                  ? 'bg-[#D4A843] text-black'
                  : 'text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="relative md:col-span-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6F6F]" />
              <Input
                placeholder="Name, phone, email..."
                value={filters.search}
                onChange={e => setF('search', e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] pl-9 text-sm"
                data-testid="admin-customers-search"
              />
            </div>
            <Select value={filters.customer_type} onValueChange={v => setF('customer_type', v)}>
              <SelectTrigger className="bg-[#111111] border-[#2A2A2A] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="old_customer">Old Customer</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="promoter">Promoter</SelectItem>
                <SelectItem value="blacklisted">Blacklisted</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2 md:col-span-2">
              <Input type="date" value={filters.date_from} onChange={e => setF('date_from', e.target.value)} className="bg-[#111111] border-[#2A2A2A] text-sm flex-1" />
              <Input type="date" value={filters.date_to} onChange={e => setF('date_to', e.target.value)} className="bg-[#111111] border-[#2A2A2A] text-sm flex-1" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[#B9B9B9] text-sm">
            <span className="text-[#F2F2F2] font-medium">{total}</span> customers
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" disabled={filters.page <= 1} onClick={() => setFilters(f => ({...f, page: f.page-1}))} className="h-7 w-7 text-[#B9B9B9]"><ChevronLeft size={14} /></Button>
            <span className="text-[#B9B9B9] text-xs">{filters.page}/{pages}</span>
            <Button variant="ghost" size="icon" disabled={filters.page >= pages} onClick={() => setFilters(f => ({...f, page: f.page+1}))} className="h-7 w-7 text-[#B9B9B9]"><ChevronRight size={14} /></Button>
          </div>
        </div>

        <div className="surface-card card-highlight rounded-xl overflow-hidden">
          {loading ? <div className="p-4"><LoadingBlock rows={6} /></div>
            : customers.length === 0 ? <EmptyState title="No customers found" description="Try adjusting filters." />
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2A2A2A]">
                      {['Customer', 'Phone', 'Email', 'Type', 'Status', 'Joined', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs text-[#6F6F6F] uppercase tracking-wider font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E1E1E]">
                    {customers.map(c => (
                      <tr
                        key={c._id}
                        className="table-row-hover cursor-pointer"
                        onClick={() => navigate(`/admin/customers/${c._id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#D4A843]/15 border border-[#D4A843]/30 flex items-center justify-center flex-shrink-0">
                              <span className="text-[#D4A843] text-xs font-semibold">
                                {c.full_name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="text-[#F2F2F2] font-medium">{c.full_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#B9B9B9]">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-[#B9B9B9]">{c.email || '—'}</td>
                        <td className="px-4 py-3">
                          <CustomerTypeBadge customerType={c.customer_type || 'basic'} />
                        </td>
                        <td className="px-4 py-3">
                          <CustomerStatusBadge status={c.status} />
                        </td>
                        <td className="px-4 py-3 text-[#6F6F6F] text-xs">{formatDateTime(c.created_at)}</td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs text-[#B9B9B9] h-7 px-2"
                              onClick={e => { e.stopPropagation(); navigate(`/admin/customers/${c._id}`); }}
                              data-testid={`customer-view-${c._id}`}
                            >
                              View
                            </Button>
                            <CustomerActionsMenu
                              customer={c}
                              currentRole={role}
                              onChange={fetchCustomers}
                              compact
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>

      <AdminCreateCustomerDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(c) => {
          if (c?._id) navigate(`/admin/customers/${c._id}`);
          else fetchCustomers();
        }}
      />
    </AdminShell>
  );
}
